import React from 'react'
import { Link } from '@tanstack/react-router'

import { Badge } from '@/components/ui/badge.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { DateFormat } from '@/components/date-format.tsx'
import {
  DocumentAttributeRender,
  isComplexDataType,
} from '@/features/documents/document-attribute-render.tsx'
import { useDocumentProcessingStatus } from '@/features/documents/hooks/use-document-processing-status.ts'
import { StatusBadge } from '@/features/documents/status-badge.tsx'
import type { DocumentsViewProps } from '@/features/documents/types.ts'
import { Thumbnail } from '@/features/files/thumbnail.tsx'
import { getProcessingStepText } from '@/routes/_authenticated/documents/-hooks/useGetProcessingStepDesc.ts'

export const DocumentsList: React.FC<DocumentsViewProps> = ({
  documents,
  uploadDateVisible = true,
  statusVisible = false,
  processingStatusVisible = false,
  leftSlot,
  onItemClick,
}) => {
  const { elapsedSecondsMap } = useDocumentProcessingStatus(
    documents,
    processingStatusVisible
  )

  return (
    <div className="grid grid-cols-1 gap-4">
      {documents?.map((document) => (
        <Link
          key={document.id}
          className="bg-card text-card-foreground flex rounded border p-4 shadow"
          to="/documents/$documentId"
          params={{ documentId: document.id }}
          onClick={(e) => onItemClick?.(e, document)}
        >
          {leftSlot?.(document)}
          <div className="mr-4 h-[150px] w-[100px] shrink-0">
            {document.jobs?.[0]?.thumbnails_processing?.status ===
            'completed' ? (
              <Thumbnail
                className="rounded border object-contain"
                fileId={document.file_id}
              />
            ) : (
              <Skeleton className="size-full" />
            )}
          </div>
          <div className="grow">
            <h3 className="mb-1 font-semibold">{document.name}</h3>

            {statusVisible && (
              <div className="mb-3 flex items-center gap-x-2">
                <StatusBadge document={document} />
                {processingStatusVisible && (
                  <div className="flex h-4 items-center text-sm text-gray-500">
                    {elapsedSecondsMap.get(document.id) ? (
                      <span>{elapsedSecondsMap.get(document.id)} secondi</span>
                    ) : null}
                    {elapsedSecondsMap.get(document.id) &&
                    getProcessingStepText(document) ? (
                      <Separator className="mx-2" orientation="vertical" />
                    ) : null}
                    {getProcessingStepText(document)}
                  </div>
                )}
              </div>
            )}

            {uploadDateVisible && (
              <p className="mb-3 text-xs text-gray-500">
                Caricato: <DateFormat date={document.updated_at} />
              </p>
            )}

            <div className="hidden flex-wrap gap-1 md:flex">
              {document.attributes?.map((documentAttribute, index) => (
                // !documentAttribute.attribute.hidden &&
                // !documentAttribute.attribute.hidden_in_list &&
                <Badge
                  key={index}
                  variant="outline"
                  className="mb-1 mr-1 inline-block max-w-80 overflow-hidden text-ellipsis text-nowrap text-xs"
                >
                  {documentAttribute.attribute.name}
                  {!isComplexDataType(
                    documentAttribute.attribute.json_schema
                  ) && (
                    <>
                      :{' '}
                      <DocumentAttributeRender
                        attributeJsonSchema={
                          documentAttribute.attribute.json_schema
                        }
                        value={documentAttribute.value}
                      />
                    </>
                  )}
                </Badge>
              ))}
            </div>
          </div>
        </Link>
      ))}
    </div>
  )
}
